import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { Menu, X, Instagram, Phone, Mail, Globe, ArrowUp } from 'lucide-react';

// Context
import { LanguageProvider, useLanguage } from './context/LanguageContext';

// Components
import CustomCursor from './components/CustomCursor';
import Seo from './components/Seo';
import { SITE_CONFIG } from './config/site';

// Lazy Loaded Pages & Features
const GalleryGrid = lazy(() => import('./features/gallery/components/GalleryGrid'));
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AdminRoutes = lazy(() => import('./features/admin/AdminRoutes'));

const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] }}
  >
    {children}
  </motion.div>
);

const useScrollThreshold = (threshold) => {
  const [isPastThreshold, setIsPastThreshold] = useState(() => (
    typeof window !== 'undefined' && window.scrollY > threshold
  ));

  useEffect(() => {
    let animationFrame = null;
    const update = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        setIsPastThreshold(window.scrollY > threshold);
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, [threshold]);

  return isPastThreshold;
};

const Footer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <footer className="py-24 border-t border-white/5 px-6 md:px-12 relative z-30 bg-cinema-black">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-16 items-center">
        <div className="text-center md:text-left flex flex-col items-center md:items-start">
          <Link to="/" className="flex items-center gap-4 group mb-4 cursor-none">
            <img src="/ab_logo.png" alt="Alister Bryan Logo" width="512" height="288" loading="lazy" decoding="async" className="h-10 md:h-12 w-auto object-contain drop-shadow-lg" />
            <span className="font-serif-clean text-3xl uppercase tracking-tighter">
              Alister <span className="text-cinema-gold">Bryan</span>
            </span>
          </Link>
          <p className="text-white/20 text-[10px] tracking-[0.4em] uppercase mb-8">Visual Storytelling / São Paulo</p>
          <div className="flex gap-6">
            <a href={SITE_CONFIG.instagramUrl} aria-label="Instagram" target="_blank" rel="noreferrer" className="text-white/30 hover:text-cinema-gold transition-colors cursor-none"><Instagram size={18} /></a>
            <a href={SITE_CONFIG.whatsappUrl} aria-label="WhatsApp" target="_blank" rel="noreferrer" className="text-white/30 hover:text-cinema-gold transition-colors cursor-none"><Phone size={18} /></a>
            <a href={`mailto:${SITE_CONFIG.email}`} aria-label="E-mail" className="text-white/30 hover:text-cinema-gold transition-colors cursor-none"><Mail size={18} /></a>
          </div>
        </div>

        <div className="text-center w-full max-w-sm mx-auto">
          <h4 className="text-white/80 text-sm font-serif-clean uppercase tracking-[0.2em] mb-4">
            {t('contact.title')}
          </h4>
          <form
            className="flex bg-white/5 p-1 border border-white/10 focus-within:border-cinema-gold/50 transition-colors"
            onSubmit={(e) => {
              e.preventDefault();
              const email = e.target.email.value;
              navigate('/contact', { state: { email } });
              e.target.reset();
            }}
          >
            <input
              type="email"
              name="email"
              required
              placeholder={t('contact.email')}
              className="bg-transparent text-white text-[10px] tracking-widest uppercase px-4 py-3 w-full outline-none placeholder:text-white/30 font-bold"
            />
            <button type="submit" className="bg-cinema-gold/80 text-black text-[9px] tracking-[0.2em] font-bold px-6 uppercase hover:bg-cinema-gold transition-colors cursor-none whitespace-nowrap">
              {t('contact.send') ? t('contact.send').split(' ')[0] : 'Send'}
            </button>
          </form>
        </div>

        <div className="text-center md:text-right flex flex-col items-center md:items-end justify-center">
          <p className="text-white/20 text-[9px] tracking-[0.4em] uppercase mb-4">© {new Date().getFullYear()} Alister Bryan</p>
          <div className="flex items-center gap-4">
            <p className="text-white/40 text-[9px] tracking-[0.3em] uppercase">All rights reserved</p>
            <span className="text-white/10 hidden md:inline">|</span>
            <Link to="/admin/dashboard" className="text-white/10 hover:text-cinema-gold text-[8px] tracking-[0.4em] uppercase transition-colors hidden md:block">Studio Access</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ScrollToTopFab = () => {
  const isVisible = useScrollThreshold(500);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={scrollToTop}
          className="scroll-to-top-fab fixed bottom-8 right-8 z-[200] p-4 bg-[#0a0a0a] border border-white/10 rounded-full text-cinema-gold hover:bg-cinema-gold hover:text-black transition-all duration-300 shadow-2xl cursor-none group"
          aria-label="Voltar ao topo"
        >
          <ArrowUp size={20} className="group-hover:-translate-y-1 transition-transform duration-300" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<div className="h-screen w-full flex items-center justify-center bg-cinema-black"><div className="w-12 h-12 border-2 border-cinema-gold/30 border-t-cinema-gold rounded-full animate-spin"></div></div>}>
        <Routes location={location} key={location.pathname}>
          {/* Public Routes */}
          <Route path="/" element={<PageTransition><Home /></PageTransition>} />
          <Route path="/gallery" element={<PageTransition><GalleryGrid /></PageTransition>} />
          <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />

          {/* Admin routes stay in a separate bundle so public visitors do not download Firebase Auth/admin code. */}
          <Route path="/admin/*" element={<PageTransition><AdminRoutes /></PageTransition>} />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

function AppContent() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isScrolled = useScrollThreshold(24);
  const { t, language, toggleLanguage } = useLanguage();
  const menuButtonRef = useRef(null);
  const menuRef = useRef(null);

  const navLinks = [
    { name: t('nav.gallery'), path: '/gallery' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
      if (event.key === 'Tab' && menuRef.current) {
        const focusable = [...menuRef.current.querySelectorAll(focusableSelector)];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);
    requestAnimationFrame(() => menuRef.current?.querySelector(focusableSelector)?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  return (
    <>
      <Seo />
      <ScrollToTop />
      <CustomCursor />

      <a href="#main-content" className="skip-link">Pular para o conteúdo</a>

      {!isAdminRoute && (
        <>
          {/* Navigation */}
          <nav aria-label="Navegação principal" className={`site-nav fixed top-0 left-0 w-full z-[150] flex justify-between items-center px-6 md:px-12 h-[88px] md:h-[104px] ${isScrolled ? 'site-nav--scrolled' : ''}`}>
            <Link
              to="/"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-4 group relative z-[160] cursor-none -ml-1"
            >
              <img src="/ab_logo.png" alt="Alister Bryan Logo" width="512" height="288" fetchPriority="high" decoding="async" className="h-8 md:h-10 w-auto object-contain drop-shadow-md" />
              <span className="font-serif-clean text-2xl uppercase tracking-tighter hidden sm:block">
                Alister <span className="text-cinema-gold transition-colors">Bryan</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-16">
              <div className="flex gap-12">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    aria-current={location.pathname === link.path ? 'page' : undefined}
                    className={`nav-link-cinema ${location.pathname === link.path ? 'nav-link-cinema--active' : ''}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              {/* Lang Toggle - Refined UI */}
              <div className="flex items-center bg-white/5 rounded-full p-1 border border-white/5">
                <button
                  onClick={() => language !== 'pt' && toggleLanguage()}
                  aria-label="Usar português"
                  aria-pressed={language === 'pt'}
                  className={`px-4 py-1.5 md:px-3 md:py-1 rounded-full text-[10px] md:text-[9px] tracking-[0.2em] transition-all duration-500 ${language === 'pt' ? 'bg-cinema-gold text-black font-bold' : 'text-white/40 hover:text-white'}`}
                >
                  PT
                </button>
                <button
                  onClick={() => language !== 'en' && toggleLanguage()}
                  aria-label="Use English"
                  aria-pressed={language === 'en'}
                  className={`px-4 py-1.5 md:px-3 md:py-1 rounded-full text-[10px] md:text-[9px] tracking-[0.2em] transition-all duration-500 ${language === 'en' ? 'bg-cinema-gold text-black font-bold' : 'text-white/40 hover:text-white'}`}
                >
                  EN
                </button>
              </div>
            </div>

            {/* Mobile Toggle */}
            <div className="flex items-center gap-4 md:hidden z-[160]">
              <div className="flex items-center bg-white/5 rounded-full border border-white/5 p-0.5">
                <button
                  onClick={() => language !== 'pt' && toggleLanguage()}
                  aria-label="Usar português"
                  aria-pressed={language === 'pt'}
                  className={`px-3 py-1.5 rounded-full text-[9px] tracking-[0.2em] transition-all ${language === 'pt' ? 'bg-cinema-gold text-black font-bold' : 'text-white/40'}`}
                >
                  PT
                </button>
                <button
                  onClick={() => language !== 'en' && toggleLanguage()}
                  aria-label="Use English"
                  aria-pressed={language === 'en'}
                  className={`px-3 py-1.5 rounded-full text-[9px] tracking-[0.2em] transition-all ${language === 'en' ? 'bg-cinema-gold text-black font-bold' : 'text-white/40'}`}
                >
                  EN
                </button>
              </div>
              <button
                ref={menuButtonRef}
                className="text-white p-2 focus:outline-none"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-navigation"
              >
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </nav>

          {/* Mobile Menu Overlay */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                ref={menuRef}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                id="mobile-navigation"
                role="dialog"
                aria-modal="true"
                aria-label="Navegação principal"
                className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[140] flex flex-col items-center justify-center pointer-events-auto"
              >
                <div className="flex flex-col gap-10 text-center">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1 * index, duration: 0.4 }}
                    >
                      <Link
                        to={link.path}
                        onClick={() => setIsMenuOpen(false)}
                        className="text-5xl md:text-7xl font-serif-clean text-white/30 hover:text-white transition-all duration-700 block"
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Global "Voltar ao Topo" Action */}
      {!isAdminRoute && <ScrollToTopFab />}

      <main id="main-content" className="relative z-10 overflow-hidden" tabIndex="-1">
        <AnimatedRoutes />
      </main>

      {!isAdminRoute && location.pathname !== '/gallery' && <Footer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <MotionConfig reducedMotion="user">
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </MotionConfig>
    </Router>
  );
}

export default App;
